import React, { useEffect, useState } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "./context/AuthContext";
import { auth } from "./firebase/firebaseConfig";
import ErrorBoundary from "./components/ErrorBoundary/ErrorBoundary";
import PainelMotorista from "./components/PainelMotorista/PainelMotorista";
import Home from "./pages/Home/Home";
import AboutPage from "./pages/AboutPage/AboutPage";
import PacotesListPage from "./pages/PacotesListPage/PacotesListPage";
import PacoteDetailPage from "./pages/PacoteDetailPage/PacoteDetailPage";
import AvaliacoesPage from "./pages/AvaliacoesPage/AvaliacoesPage";
import Destinos from "./pages/Destinos/Destinos";
import Contato from "./pages/Contato/Contato";
import BlogPage from "./pages/BlogPage/BlogPage";
import BlogPostPage from "./pages/BlogPage/BlogPostPage";
import AdminLogin from "./components/Admin/AdminLogin/AdminLogin";
import AdminDashboard from "./components/Admin/AdminDashboard/AdminDashboard";
import AdminPacotes from "./components/AdminPacotes/AdminPacotes";
import AdminEditPacote from "./components/Admin/AdminEditPacote/AdminEditPacote";
import AdminBlog from "./components/AdminBlog/AdminBlog";
import AdminParceiros from "./components/AdminParceiros/AdminParceiros";
import ParceiroDetailPage from "./pages/ParceiroDetailPage/ParceiroDetailPage";
import ParceirosPage from "./pages/ParceirosPage/ParceirosPage";
import EditHeroSlides from "./components/Admin/EditHeroSlides/EditHeroSlides";
import ViewUsers from "./components/Admin/Users/ViewUsers";
import EditHeader from "./components/Admin/EditHeader/EditHeader";
import EditBanner from "./components/Admin/EditBanner/EditBanner";
import EditBoxes from "./components/Admin/EditBoxes/EditBoxes";
import EditAbout from "./components/Admin/EditAbout/EditAbout";
import EditFooter from "./components/Admin/EditFooter/EditFooter";
import AdminWhatsAppConfig from "./components/Admin/AdminWhatsAppConfig/AdminWhatsAppConfig";
import EditCarousel from "./components/Admin/EditCarousel/EditCarousel";
import EditHours from "./components/Admin/EditHours/EditHours";
import BannerAdmin from "./components/Admin/BannerAdmin/BannerAdmin";
import LoadingOverlay from "./components/LoadingOverlay/LoadingOverlay";
import AuthMotorista from "./components/AuthMotorista/AuthMotorista";
import AuthUsuario from "./components/AuthUsuario/AuthUsuario";
import PainelUsuario from "./components/Usuario/PainelUsuario";
import ReservasPage from "./pages/Reservas/Reservas";
import Sucesso from "./pages/Sucesso/Sucesso";
import ViagemManager from "./components/ViagemManager/ViagemManager";
import { autoInitialize } from "./utils/firestoreUtils";

// Contexto para controle global do loading
export const LoadingContext = React.createContext();

const ProtectedRoute = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const { setLoading: setGlobalLoading } = React.useContext(LoadingContext);

  useEffect(() => {
    setGlobalLoading(true);
    const unsubscribe = auth.onAuthStateChanged((user) => {
      setUser(user);
      setLoading(false);
      setGlobalLoading(false);
    });
    return () => unsubscribe();
  }, [setGlobalLoading]);

  if (loading) return null;
  return user ? children : <Navigate to="/admin/login" />;
};

const App = () => {
  const [loading, setLoading] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);
  const [contentReady, setContentReady] = useState(false);

  useEffect(() => {
    // Inicializa estruturas do Firestore automaticamente
    const initializeApp = async () => {
      try {
        await autoInitialize();
        // Marca que o conteúdo está pronto
        setContentReady(true);
        // Pequeno delay para transição suave (300ms)
        setTimeout(() => {
          setInitialLoad(false);
        }, 300);
      } catch (error) {
        console.error('Erro ao inicializar:', error);
        setContentReady(true);
        setInitialLoad(false);
      }
    };

    initializeApp();
  }, []);

  return (
    <LoadingContext.Provider value={{ setLoading }}>
      <AuthProvider>
        <Router>
          {(loading || initialLoad) && <LoadingOverlay />}
          <ErrorBoundary>
            <Routes>
              {/* Motorista - Login, Cadastro e Painel */}
              <Route path="/motorista/login" element={<AuthMotorista />} />
              <Route path="/motorista/cadastro" element={<AuthMotorista />} />
              <Route path="/painel-motorista" element={<PainelMotorista />} />
              
              {/* Rotas Públicas */}
              <Route path="/" element={<Home />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/pacotes" element={<PacotesListPage />} />
              <Route path="/reservas" element={<ReservasPage />} />
              <Route path="/obrigado" element={<Sucesso />} />
              <Route path="/pacote/:pacoteSlug" element={<PacoteDetailPage />} />
              <Route path="/parceiros" element={<ParceirosPage />} />
              <Route path="/parceiro/:id" element={<ParceiroDetailPage />} />
              <Route path="/avaliacoes" element={<AvaliacoesPage />} />
              <Route path="/destinos" element={<Destinos />} />
              <Route path="/contato" element={<Contato />} />
              <Route path="/blog" element={<BlogPage />} />
              <Route path="/blog/:slug" element={<BlogPostPage />} />

              {/* Rotas Administrativas */}
              <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/admin/dashboard" element={<Navigate to="/admin" />} />
              <Route path="/admin/pacotes" element={<ProtectedRoute><AdminPacotes /></ProtectedRoute>} />
              <Route path="/admin/pacotes/editar/:pacoteId" element={<ProtectedRoute><AdminEditPacote /></ProtectedRoute>} />
              <Route path="/admin/parceiros" element={<ProtectedRoute><AdminParceiros /></ProtectedRoute>} />
              <Route path="/admin/viagens" element={<ProtectedRoute><ViagemManager /></ProtectedRoute>} />
              <Route path="/admin/blog" element={<ProtectedRoute><AdminBlog /></ProtectedRoute>} />
              <Route path="/admin/hero-slides" element={<ProtectedRoute><EditHeroSlides /></ProtectedRoute>} />
              <Route path="/admin/edit-header" element={<ProtectedRoute><EditHeader /></ProtectedRoute>} />
              <Route path="/admin/edit-banner" element={<ProtectedRoute><EditBanner /></ProtectedRoute>} />
              <Route path="/admin/edit-boxes" element={<ProtectedRoute><EditBoxes /></ProtectedRoute>} />
              <Route path="/admin/edit-about" element={<ProtectedRoute><EditAbout /></ProtectedRoute>} />
              <Route path="/admin/edit-footer" element={<ProtectedRoute><EditFooter /></ProtectedRoute>} />
              <Route path="/admin/edit-whatsapp" element={<ProtectedRoute><AdminWhatsAppConfig /></ProtectedRoute>} />
              <Route path="/admin/edit-carousel" element={<ProtectedRoute><EditCarousel /></ProtectedRoute>} />
              <Route path="/admin/edit-hours" element={<ProtectedRoute><EditHours /></ProtectedRoute>} />
              <Route path="/admin/banner-admin" element={<ProtectedRoute><BannerAdmin /></ProtectedRoute>} />
              <Route path="/admin/view-users" element={<ProtectedRoute><ViewUsers /></ProtectedRoute>} />
              
              {/* Usuários */}
              <Route path="/usuario" element={<AuthUsuario />} />
              <Route path="/usuario/auth" element={<AuthUsuario />} />
              <Route path="/usuario/painel" element={<PainelUsuario />} />
              
              <Route path="*" element={<Navigate to="/" />} />
            </Routes>
          </ErrorBoundary>
        </Router>
      </AuthProvider>
    </LoadingContext.Provider>
  );
};

export default App;